import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import type { SignaturePlace } from "@secure-caseworker/shared";
import type { SignerRole, RoutingType } from "../types/request";
import { createDefaultSigner } from "../views/RequestTemplates/components/SignerRolesEditor";
import { useToast } from "./ToastContext";

/**
 * Extended PdfDocument type including client-side properties.
 * Order is determined by array index - no separate order field needed.
 */
export interface BrowserPdfDocument {
  id?: string; // Only set for existing docs from backend
  file?: File; // Only set for newly uploaded files
  previewUrl?: string; // For local preview (loaded by EditRequestTemplatePage for existing PDFs)
  title: string;
  signaturePlaces: SignaturePlace[];
}

/**
 * Alert callback type
 */
export interface AlertOptions {
  variant: "error" | "success" | "warning" | "info";
  title: string;
  message: string;
}

/**
 * Context for managing PDF document state in signature templates
 * Handles upload, preview, signature configuration, signer roles, and file management
 */
interface PdfManagementContextType {
  // State
  pdfDocuments: BrowserPdfDocument[];
  currentDocumentIndex: number;
  showSignatureModal: boolean;

  // Signer roles state
  signerRoles: SignerRole[];
  routing: RoutingType;

  // State setters (for UI control)
  setCurrentDocumentIndex: (index: number) => void;
  setShowSignatureModal: (show: boolean) => void;
  setSignerRoles: (signers: SignerRole[]) => void;
  setRouting: (routing: RoutingType) => void;

  // Handlers - all use array index for lookups
  handlePdfFilesChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  handleRemoveDocument: (index: number) => void;
  handleUpdateDocumentTitle: (index: number, title: string) => void;
  handleMoveDocumentUp: (index: number) => void;
  handleMoveDocumentDown: (index: number) => void;
  handleUpdateSignaturePlaces: (places: SignaturePlace[]) => void;

  // Getters
  getPdfUrl: () => string | undefined;
  getSignaturePlaces: () => SignaturePlace[];
}

/**
 * Provider props
 */
interface PdfManagementProviderProps {
  children: React.ReactNode;
  initialPdfDocuments?: BrowserPdfDocument[];
  initialSignerRoles?: SignerRole[];
  initialRouting?: RoutingType;
}

const PdfManagementContext = createContext<PdfManagementContextType | null>(
  null
);

/**
 * Provider component for PDF management context
 */
export const PdfManagementProvider: React.FC<PdfManagementProviderProps> = ({
  children,
  initialPdfDocuments,
  initialSignerRoles,
  initialRouting,
}) => {
  const { showToast } = useToast();
  // Multi-PDF signature state - initialize from props (preview URLs already loaded)
  const [pdfDocuments, setPdfDocuments] = useState<BrowserPdfDocument[]>(
    initialPdfDocuments ?? []
  );
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // Track URLs created locally (for new uploads) so we can clean them up
  const localPreviewUrlsRef = useRef<Set<string>>(new Set());

  // Signer roles state - default to one signer if not provided
  const [signerRoles, setSignerRoles] = useState<SignerRole[]>(
    initialSignerRoles && initialSignerRoles.length > 0
      ? initialSignerRoles
      : [createDefaultSigner(1)]
  );
  const [routing, setRouting] = useState<RoutingType>(
    initialRouting || "parallel"
  );

  // Clean up locally created preview URLs on unmount
  useEffect(() => {
    const localUrls = localPreviewUrlsRef.current;
    return () => {
      localUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  // Open signature modal after PDF documents have been updated
  useEffect(() => {
    if (pendingModalOpen && pdfDocuments.length > 0) {
      console.log(pdfDocuments, "useEffect");
      setPendingModalOpen(false);
      setShowSignatureModal(true);
    }
  }, [pdfDocuments, pendingModalOpen]);

  const handlePdfFilesChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate max 5 PDFs total
    if (pdfDocuments.length + files.length > 5) {
      showToast("Maximum 5 PDF documents allowed per template", "error");
      return;
    }

    // Validate each file
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    for (const file of files) {
      if (file.type !== "application/pdf") {
        showToast(`${file.name} is not a PDF file`, "error");

        return;
      }

      if (file.size > maxSize) {
        showToast(`${file.name} must be smaller than 10MB`, "error");

        return;
      }
    }

    // Add new PDF documents
    const newDocuments: BrowserPdfDocument[] = files.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      // Track locally created URLs for cleanup
      localPreviewUrlsRef.current.add(previewUrl);
      return {
        title: file.name.replace(".pdf", ""),
        signaturePlaces: [],
        file,
        previewUrl,
      };
    });

    const newPdfDocuments = [...pdfDocuments, ...newDocuments];
    setPdfDocuments(newPdfDocuments);
    // Set index for first new document and flag to open modal after state updates
    setCurrentDocumentIndex(pdfDocuments.length);
    setPendingModalOpen(true);
  };

  const handleRemoveDocument = (index: number) => {
    const doc = pdfDocuments[index];
    // Only revoke URLs we created locally
    if (doc?.previewUrl && localPreviewUrlsRef.current.has(doc.previewUrl)) {
      URL.revokeObjectURL(doc.previewUrl);
      localPreviewUrlsRef.current.delete(doc.previewUrl);
    }

    setPdfDocuments(pdfDocuments.filter((_, i) => i !== index));
  };

  const handleUpdateDocumentTitle = (index: number, title: string) => {
    setPdfDocuments(
      pdfDocuments.map((doc, i) => (i === index ? { ...doc, title } : doc))
    );
  };

  const handleMoveDocumentUp = (index: number) => {
    if (index === 0) return;
    const newDocs = [...pdfDocuments];
    [newDocs[index - 1], newDocs[index]] = [newDocs[index], newDocs[index - 1]];
    setPdfDocuments(newDocs);
  };

  const handleMoveDocumentDown = (index: number) => {
    if (index === pdfDocuments.length - 1) return;
    const newDocs = [...pdfDocuments];
    [newDocs[index], newDocs[index + 1]] = [newDocs[index + 1], newDocs[index]];
    setPdfDocuments(newDocs);
  };

  /**
   * Update signature places in local state only (no API call).
   * API persistence happens on form submit.
   */
  const handleUpdateSignaturePlaces = (places: SignaturePlace[]) => {
    setPdfDocuments(
      pdfDocuments.map((doc, i) =>
        i === currentDocumentIndex ? { ...doc, signaturePlaces: places } : doc
      )
    );
  };

  const getPdfUrl = () => pdfDocuments[currentDocumentIndex].previewUrl;
  const getSignaturePlaces = () =>
    pdfDocuments[currentDocumentIndex].signaturePlaces;

  const value: PdfManagementContextType = {
    pdfDocuments,
    currentDocumentIndex,
    showSignatureModal,
    signerRoles,
    routing,
    setCurrentDocumentIndex,
    setShowSignatureModal,
    setSignerRoles,
    setRouting,
    handlePdfFilesChange,
    handleRemoveDocument,
    handleUpdateDocumentTitle,
    handleMoveDocumentUp,
    handleMoveDocumentDown,
    handleUpdateSignaturePlaces,
    getPdfUrl,
    getSignaturePlaces,
  };

  return (
    <PdfManagementContext.Provider value={value}>
      {children}
    </PdfManagementContext.Provider>
  );
};

/**
 * Hook to access PDF management context
 * Must be used within PdfManagementProvider
 */
export const usePdfManagement = () => {
  const context = useContext(PdfManagementContext);
  if (!context) {
    throw new Error(
      "usePdfManagement must be used within PdfManagementProvider"
    );
  }
  return context;
};
